import { Autocomplete, CircularProgress, Container, Grid, Typography } from "@mui/material";
import { AsolviDatePicker } from "components/AsolviDatePicker";
import barcodeScanner from "components/barcodeScan/barcodeScanner";
import { DateFilterType, FilterDateSelect } from "components/FilterDateSelect";
import FullScreenDialog from "components/FullScreenDialog";
import { MachineAutocomplete } from "components/machine/MachineAutocomplete";
import PrimaryButton from "components/PrimaryButton";
import SecondaryButton from "components/SecondaryButton";
import StyledTextField from "components/StyledTextField";
import { endOfDay } from "date-fns";
import { addressToPostalLocation } from "helpers";
import { useDebounce } from "hooks/useDebounce";
import {
  HistoryFilterDateTypes,
  SearchHistoryFilter,
  SearchHistoryFilterStorageKey,
} from "models/HistoryFilter";
import { MachineType } from "operations/schema/schema";
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import {
  getCustomer,
  getCustomerByMachine,
  getCustomers,
  resetSearchHistoryFilter,
  setSearchHistoryFilter,
} from "store/slices/history.store";

type SearchHistoryFilterDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const SearchHistoryFilterDialog: FC<SearchHistoryFilterDialogProps> = (props) => {
  const { open, setOpen } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const {
    searchHistoryFilter: filter,
    customers,
    customersLoading,
  } = useAppSelector((state) => state.history);
  const [customerSearch, setCustomerSearch] = useState<string>("");
  const debouncedCustomerSearch = useDebounce<string>(customerSearch, 500);

  const dateOptions: DateFilterType[] = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: "filter.last7days" }),
        value: "week",
      },
      {
        label: intl.formatMessage({ id: "filter.lastMonth" }),
        value: "1month",
      },
      {
        label: intl.formatMessage({ id: "filter.last12months" }),
        value: "12month",
      },
      {
        label: intl.formatMessage({ id: "filter.last24months" }),
        value: "24month",
      },
      {
        label: intl.formatMessage({ id: "filter.last5years" }),
        value: "5year",
      },
      {
        label: intl.formatMessage({ id: "filter.specificDate" }),
        value: "specificDate",
      },
    ],
    [intl],
  );

  const setFilter = useCallback(
    (filter: SearchHistoryFilter) => {
      dispatch(setSearchHistoryFilter({ searchHistoryFilter: filter }));
    },
    [dispatch],
  );
  const resetFilter = useCallback(() => {
    dispatch(resetSearchHistoryFilter());
  }, [dispatch]);
  const saveFilter = useCallback(() => {
    localStorage.setItem(SearchHistoryFilterStorageKey, JSON.stringify(filter));
  }, [filter]);

  const setCustomer = useCallback(
    async (
      id: string | null | undefined,
      name: string | null | undefined,
      machineGuid: string | null | undefined,
    ) => {
      if (id) {
        let customer = customers.find((x) => x.id === id);
        if (!customer) {
          if (machineGuid) {
            await dispatch(getCustomerByMachine({ machineGuid: machineGuid }));
          } else if (name) {
            await dispatch(getCustomer({ nameFilter: name }));
          }
          customer = customers.find((x) => x.id === id);
        }
        if (customer) {
          setFilter({
            ...filter,
            customer,
          });
        } else {
          console.log("Something went wrong");
        }
      }
    },
    [customers, dispatch, filter, setFilter],
  );

  const setDate = useCallback(
    (type: HistoryFilterDateTypes) => {
      var specificDate = null;
      if (type === "specificDate") specificDate = filter.specificDate;
      setFilter({
        ...filter,
        typeDate: type,
        specificDate: specificDate,
      });
    },
    [filter, setFilter],
  );
  const setSpecificDate = useCallback(
    (value: Date | null) => {
      setFilter({
        ...filter,
        specificDate: value ? endOfDay(value) : null,
        typeDate: "specificDate",
      });
    },
    [filter, setFilter],
  );

  useEffect(() => {
    if (open) {
      dispatch(getCustomers({ nameFilter: debouncedCustomerSearch }));
    }
  }, [debouncedCustomerSearch, dispatch, open]);

  useEffect(() => {
    if (open) {
      if (filter.machine?.id) {
        if (filter.machine.customerId && filter.customer?.id !== filter.machine.customerId) {
          setCustomer(filter.machine.customerId, filter.machine.customerName, filter.machine.id);
        }
      }
    }
  }, [open, setCustomer, dispatch, filter]);

  const onSetMachine = useCallback(
    (value: MachineType | null) => {
      setFilter({
        ...filter,
        machine: value,
      });
    },
    [filter, setFilter],
  );

  useEffect(() => {
    barcodeScanner.initialize();
    return () => {
      barcodeScanner.turnOff();
    };
  }, []);

  return (
    <FullScreenDialog
      title={intl.formatMessage({ id: "filter.setFilters" })}
      open={open}
      setOpen={setOpen}
      child={
        <Container sx={{ mt: 2 }}>
          <Grid container spacing={1}>
            <Grid item container alignItems="start" justifyContent="space-between" wrap="nowrap">
              <Grid item width="100%">
                <MachineAutocomplete customerId={filter.customer?.id} onChange={onSetMachine} />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                data-testid="SearchHistoryFilterDialog-CustomerFilter"
                disableClearable={!!filter.machine?.id}
                value={filter.customer}
                onChange={(_, value, reason) => {
                  if (reason === "clear") {
                    setFilter({
                      ...filter,
                      customer: null,
                    });
                  } else {
                    if (value) {
                      setFilter({
                        ...filter,
                        customer: value,
                      });
                    }
                  }
                }}
                getOptionLabel={(o) => `${o.name}`}
                renderOption={(props, option) => (
                  <li {...props} key={`${option.id}-${option.name}`} style={{ display: "block" }}>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {option.customerNo}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {addressToPostalLocation(option.address)}
                    </Typography>
                  </li>
                )}
                inputValue={customerSearch}
                onInputChange={(_, value) => {
                  setCustomerSearch(value);
                }}
                isOptionEqualToValue={(option, value) =>
                  value && value.id ? option.id === value.id : true
                }
                filterOptions={(x) => x}
                options={customers}
                loading={customersLoading}
                renderInput={(params) => (
                  <StyledTextField
                    name="customer"
                    {...params}
                    label={intl.formatMessage({
                      id: "general.customer",
                    })}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <Fragment>
                          {customersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </Fragment>
                      ),
                    }}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FilterDateSelect value={filter.typeDate} options={dateOptions} onChange={setDate} />
            </Grid>
            {filter.typeDate === "specificDate" && (
              <Grid item xs={12}>
                <AsolviDatePicker
                  data-testid="ServiceHistoryFilterDialog-DatePickerFilter"
                  onChange={(value) => {
                    setSpecificDate(value);
                  }}
                  value={filter.specificDate}
                  label={intl.formatMessage({ id: "filter.specificDate" })}
                  clearable
                />
              </Grid>
            )}

            <Grid item xs={12} mt={2}>
              <SecondaryButton
                onClick={resetFilter}
                data-testid="SearchHistoryFilterDialog-ResetButton"
              >
                <FormattedMessage id="filter.reset" />
              </SecondaryButton>
            </Grid>
            <Grid item xs={12}>
              <PrimaryButton
                data-testid="SearchHistoryFilterDialog-SubmitButton"
                onClick={() => {
                  saveFilter();
                  setOpen(false);
                }}
              >
                <FormattedMessage id={"history.viewServiceHistory"} />
              </PrimaryButton>
            </Grid>
          </Grid>
        </Container>
      }
    />
  );
};
